import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { CollapsibleSection } from 'components/CollapsibleSection';
import useCallbackRef from 'hooks/useCallbackRef';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';
import { useTranslation } from 'react-i18next';
import Archive from './Archive';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { DataRoomArchiveUpdatedEventData } from 'services/socketService/socketEventData';
import { DataRoomArchive } from './types';
import { checkIsArchiveCompleted } from './utils';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { MAX_LIMIT } from 'data/global';
import GlobeIcon from 'icons/Globe.icon';
import BagIcon from 'icons/Bag.icon';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import CreateIndexDrawer from './CreateIndexDrawer';
import useEffectSkipFirst from 'hooks/useEffectSkipFirst';
import useRefetch from 'hooks/useRefetch';

import './Snapshots.styles.scss';

type SnapshotsProps = {
  className?: string;
};

const Snapshots: ForwardRefRenderFunction<CollapsibleRef, SnapshotsProps> = (
  props,
  ref,
) => {
  const { className } = props;

  const [isFetching, setIsFetching] = useState(false);
  const [acrhives, setAcrhives] = useState<DataRoomArchive[]>([]);

  const [snapshots, snapshotsRef] = useCallbackRef<CollapsibleRef>(null);

  const [fullDrDrawer, fullDrdrawerRef] = useCallbackRef<SideDrawerRef>(null);
  const [buySideDrawer, buySideDrawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { buySideGroups } = useContext(MembersContext);

  const classes = classNames('snapshots', 'white-panel', className);

  const {
    dataRoom: { _id: dataRoomId },
    doesntHaveAccess,
  } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const showGenerateButton = useMemo(
    () =>
      !isFetching && acrhives.every((a) => checkIsArchiveCompleted(a.lastStep)),
    [acrhives, isFetching],
  );

  const showBuySideGenerateButton = useMemo(
    () => showGenerateButton && !!buySideGroups?.length,
    [buySideGroups?.length, showGenerateButton],
  );

  const { t } = useTranslation();

  const { refetchFlag, refetch } = useRefetch();

  useImperativeHandle(ref, () => snapshots, [snapshots]);

  const addArchive = useCallback(
    (archive: DataRoomArchive) => {
      setAcrhives((prev) => [archive, ...prev]);
      refetch();
    },
    [refetch],
  );

  const isCollapsible = !!acrhives.length;

  const triggerTitle = useMemo(
    () => (
      <div className="snapshots__title">
        <p className="name">{t('dataRoomArchives')}</p>
        <p>{t('dataRoomArchivesDescription')}</p>

        <div>
          {showGenerateButton && (
            <Button
              variant="link"
              disabled={doesntHaveAccess}
              icon={GlobeIcon}
              onClick={(e) => {
                e.stopPropagation();
                fullDrDrawer?.setOpen(true);
              }}
            >
              {t('generateFullDataRoom')}
            </Button>
          )}
          {showBuySideGenerateButton && (
            <Button
              variant="link"
              disabled={doesntHaveAccess}
              icon={BagIcon}
              onClick={(e) => {
                e.stopPropagation();
                buySideDrawer?.setOpen(true);
              }}
            >
              {t('createIndexSpecificBuyGroup')}
            </Button>
          )}
        </div>
      </div>
    ),
    [
      showGenerateButton,
      doesntHaveAccess,
      showBuySideGenerateButton,
      fullDrDrawer,
      buySideDrawer,
      t,
    ],
  );

  const drawers = useMemo(
    () => (
      <>
        {showGenerateButton && (
          <CreateIndexDrawer ref={fullDrdrawerRef} addArchive={addArchive} />
        )}
        {showBuySideGenerateButton && (
          <CreateIndexDrawer
            ref={buySideDrawerRef}
            buySideGroups={buySideGroups}
            addArchive={addArchive}
          />
        )}
      </>
    ),
    [
      showBuySideGenerateButton,
      showGenerateButton,
      buySideGroups,
      addArchive,
      buySideDrawerRef,
      fullDrdrawerRef,
    ],
  );

  const updateArchive = useCallback(
    (archive: DataRoomArchive) =>
      setAcrhives((prev) =>
        prev.map((el) =>
          el._id !== archive._id
            ? el
            : {
                ...el,
                ...archive,
              },
        ),
      ),
    [],
  );

  const socketUpdateArchive = useCallback(
    (eventData: DataRoomArchiveUpdatedEventData) => {
      const { _id, lastStep, stepsCompletedDates } = eventData;

      setAcrhives((prev) =>
        prev.map((a) =>
          a._id !== _id
            ? a
            : {
                ...a,
                lastStep: checkIsArchiveCompleted(a.lastStep)
                  ? a.lastStep
                  : lastStep,
                stepsCompletedDates:
                  a.stepsCompletedDates.length > stepsCompletedDates.length
                    ? a.stepsCompletedDates
                    : stepsCompletedDates,
              },
        ),
      );
    },
    [],
  );

  useSocketEvent(LISTEN_EVENTS.DATA_ROOM_ARCHIVE_UPDATED, socketUpdateArchive);

  useEffect(() => {
    (async () => {
      try {
        setIsFetching(true);
        const {
          data: { items },
        } = await dataRoomApi.archives.getArchives(dataRoomId, {
          $limit: MAX_LIMIT,
        });
        setAcrhives(items);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [dataRoomId, popServerError]);

  useEffectSkipFirst(() => {
    if (acrhives?.length) snapshots?.setOpen(true);
  }, [refetchFlag]);

  if (!isCollapsible)
    return (
      <div className={classNames(classes, 'white-panel')}>
        {triggerTitle}
        {drawers}
      </div>
    );

  return (
    <>
      <CollapsibleSection
        className={classes}
        ref={snapshotsRef}
        trigger={<div className="trigger__content">{triggerTitle}</div>}
      >
        <div className="snapshots__archives">
          {acrhives.map((archive) => (
            <Archive
              key={archive._id}
              archive={archive}
              updateArchive={updateArchive}
            />
          ))}
        </div>
      </CollapsibleSection>

      {drawers}
    </>
  );
};

export default forwardRef(Snapshots);
